import { TextFieldModule } from "@angular/cdk/text-field";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AuthService } from "app/core/auth/auth.service";
import { finalize } from "rxjs";
import { SupportRequestService } from "./support-request.service";

export const MAX_SUPPORT_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MIN_SUPPORT_MESSAGE_LENGTH = 5;
export const SUPPORT_ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "application/rtf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/zip",
  "application/x-rar-compressed",
] as const;

function trimmedMinLength(minLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = typeof control.value === "string" ? control.value : "";
    const actualLength = value.trim().length;

    return actualLength >= minLength
      ? null
      : {
          trimmedMinLength: {
            actualLength,
            requiredLength: minLength,
          },
        };
  };
}

@Component({
  selector: "support-request-dialog",
  templateUrl: "./support-request-dialog.component.html",
  styleUrls: ["./support-request-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TextFieldModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
})
export class SupportRequestDialogComponent {
  private readonly _authService = inject(AuthService);
  private readonly _dialogRef = inject(
    MatDialogRef<SupportRequestDialogComponent>,
  );
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _supportRequestService = inject(SupportRequestService);

  readonly acceptedFileTypes = SUPPORT_ATTACHMENT_MIME_TYPES.join(",");
  readonly minimumMessageLength = MIN_SUPPORT_MESSAGE_LENGTH;
  readonly form = this._formBuilder.nonNullable.group({
    email: [
      this._authService.getUser()?.email?.trim() ?? "",
      [Validators.required, Validators.email],
    ],
    comments: [
      "",
      [Validators.required, trimmedMinLength(MIN_SUPPORT_MESSAGE_LENGTH)],
    ],
  });

  selectedFile: File | null = null;
  attachmentError = "";
  submitError = "";
  isSubmitting = false;

  get commentsControl() {
    return this.form.controls.comments;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.item(0) ?? null;
    input.value = "";
    this.attachmentError = "";

    if (!file) {
      return;
    }

    if (
      !SUPPORT_ATTACHMENT_MIME_TYPES.includes(
        file.type as (typeof SUPPORT_ATTACHMENT_MIME_TYPES)[number],
      )
    ) {
      this.selectedFile = null;
      this.attachmentError =
        "Choose a supported document, image, or archive file.";
      return;
    }

    if (file.size > MAX_SUPPORT_ATTACHMENT_BYTES) {
      this.selectedFile = null;
      this.attachmentError = "The attachment must be 10 MB or smaller.";
      return;
    }

    this.selectedFile = file;
  }

  removeAttachment(): void {
    this.selectedFile = null;
    this.attachmentError = "";
  }

  close(): void {
    if (!this.isSubmitting) {
      this._dialogRef.close();
    }
  }

  submit(): void {
    this.submitError = "";
    this.form.markAllAsTouched();

    if (this.form.invalid || this.attachmentError || this.isSubmitting) {
      return;
    }

    const { email, comments } = this.form.getRawValue();
    this.isSubmitting = true;
    this._dialogRef.disableClose = true;

    this._supportRequestService
      .createTicket({
        email,
        comments,
        document: this.selectedFile ?? undefined,
      })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this._dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          this._snackBar.open("Support request sent successfully.", "Close", {
            duration: 4000,
            panelClass: ["success-snackbar"],
          });
          this._dialogRef.close(true);
        },
        error: () => {
          this.submitError =
            "We could not send your request. Please try again.";
        },
      });
  }
}
